import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  CookieConsentService,
  CookiePreferences,
  ConsentState,
} from '../../../core/services/cookie-consent.service';

@Component({
  selector: 'app-cookie-preferences-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-overlay" [class.show]="isOpen" (click)="closeModal()" *ngIf="isOpen">
      <div
        class="modal-content"
        (click)="$event.stopPropagation()"
        role="dialog"
        aria-labelledby="modal-title"
      >
        <div class="modal-header">
          <h2 id="modal-title" class="modal-title">Cookie Preferences</h2>
          <button type="button" class="close-btn" (click)="closeModal()" aria-label="Close modal">
            ×
          </button>
        </div>

        <div class="modal-body">
          <p class="modal-description">
            Manage your cookie preferences. You can enable or disable different types of cookies
            below.
          </p>

          <div class="cookie-categories">
            <!-- Necessary Cookies -->
            <div class="cookie-category">
              <div class="cookie-category__header">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    [checked]="preferences.necessary"
                    disabled
                    aria-describedby="necessary-desc"
                  />
                  <span class="checkbox-custom"></span>
                  <div class="category-info">
                    <span class="category-title">Necessary Cookies</span>
                    <span class="category-badge">Always Active</span>
                  </div>
                </label>
              </div>
              <p id="necessary-desc" class="category-description">
                Essential for the website to function properly. These cannot be disabled.
              </p>
            </div>

            <!-- Analytics Cookies -->
            <div class="cookie-category">
              <div class="cookie-category__header">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    [(ngModel)]="preferences.analytics"
                    aria-describedby="analytics-desc"
                  />
                  <span class="checkbox-custom"></span>
                  <div class="category-info">
                    <span class="category-title">Analytics Cookies</span>
                  </div>
                </label>
              </div>
              <p id="analytics-desc" class="category-description">
                Help us understand how visitors interact with our website by collecting anonymous
                information.
              </p>
            </div>

            <!-- Marketing Cookies -->
            <div class="cookie-category">
              <div class="cookie-category__header">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    [(ngModel)]="preferences.marketing"
                    aria-describedby="marketing-desc"
                  />
                  <span class="checkbox-custom"></span>
                  <div class="category-info">
                    <span class="category-title">Marketing Cookies</span>
                  </div>
                </label>
              </div>
              <p id="marketing-desc" class="category-description">
                Used to track visitors across websites to display relevant advertisements.
              </p>
            </div>

            <!-- Functional Cookies -->
            <div class="cookie-category">
              <div class="cookie-category__header">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    [(ngModel)]="preferences.functional"
                    aria-describedby="functional-desc"
                  />
                  <span class="checkbox-custom"></span>
                  <div class="category-info">
                    <span class="category-title">Functional Cookies</span>
                  </div>
                </label>
              </div>
              <p id="functional-desc" class="category-description">
                Enable enhanced functionality like chat widgets and personalized content.
              </p>
            </div>
          </div>

          <div class="consent-info" *ngIf="consentDate">
            <small class="consent-date"> Last updated: {{ consentDate | date: 'medium' }} </small>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn--outline" (click)="closeModal()">Cancel</button>

          <button type="button" class="btn btn--secondary" (click)="acceptNecessaryOnly()">
            Necessary Only
          </button>

          <button type="button" class="btn btn--primary" (click)="savePreferences()">
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./cookie-preferences-modal.component.scss'],
})
export class CookiePreferencesModalComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() closeEvent = new EventEmitter<void>();

  preferences: CookiePreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  };

  consentDate: string = '';
  private subscription?: Subscription;

  constructor(private cookieService: CookieConsentService) {}

  ngOnInit(): void {
    // Load current preferences
    this.subscription = this.cookieService.consent$.subscribe((state: ConsentState) => {
      this.preferences = { ...state.preferences };
      this.consentDate = state.consentDate;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }

  closeModal(): void {
    this.closeEvent.emit();
  }

  acceptNecessaryOnly(): void {
    this.cookieService.acceptNecessaryOnly();
    this.closeModal();
  }

  savePreferences(): void {
    this.cookieService.saveCustomPreferences(this.preferences);
    this.closeModal();
  }
}
